import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const UserSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('password');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');

  // Check if user logged in via Google (no password)
  const isGoogleUser = user?.auth_provider === 'google';

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [emailForm, setEmailForm] = useState({
    new_email: '',
    password: ''
  });

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('New passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // For Google users, don't send current_password
      const payload = isGoogleUser 
        ? { new_password: passwordForm.new_password }
        : {
            current_password: passwordForm.current_password,
            new_password: passwordForm.new_password
          };
      
      await authAPI.updatePassword(payload);
      setSuccess(isGoogleUser ? 'Password set successfully!' : 'Password updated successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await authAPI.updateEmail(emailForm);
      setSuccess('Email updated successfully! Please login again.');
      setTimeout(() => {
        onLogout();
        navigate('/');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await authAPI.setup2FA();
      setTwoFASetup(response.data);
      setSuccess('Scan the QR code with your authenticator app');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await authAPI.enable2FA({ code: twoFACode });
      setSuccess('2FA enabled successfully!');
      setTwoFASetup(null);
      setTwoFACode('');
      // Refresh user data
      window.location.reload();
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await authAPI.disable2FA({ code: twoFACode });
      setSuccess('2FA disabled successfully!');
      setTwoFACode('');
      window.location.reload();
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <nav className="navbar px-6 py-4 border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src="/logo-light.png" alt="Dexreaction" className="h-10" />
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/dashboard')} className="btn-secondary">
              Dashboard
            </button>
            <button onClick={onLogout} className="btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-8">Account Settings</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-purple-500/20">
          <button
            onClick={() => setActiveTab('password')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'password'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Change Password
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'email'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Change Email
          </button>
          <button
            onClick={() => setActiveTab('pin')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'pin'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Support PIN
          </button>
          <button
            onClick={() => setActiveTab('2fa')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === '2fa'
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            2FA Security
          </button>
        </div>

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="reaction-card">
            <h2 className="text-2xl font-bold mb-6">
              {isGoogleUser ? 'Set Password' : 'Change Password'}
            </h2>
            {isGoogleUser && (
              <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-gray-300">
                  <span className="font-semibold text-blue-400">ℹ️ Google Account:</span> You logged in with Google. 
                  Set a password to enable email/password login as an alternative.
                </p>
              </div>
            )}
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              {!isGoogleUser && (
                <div>
                  <label className="block text-sm font-medium mb-2">Current Password</label>
                  <input
                    type="password"
                    className="input-field"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {isGoogleUser ? 'New Password' : 'New Password'}
                </label>
                <input
                  type="password"
                  className="input-field"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <input
                  type="password"
                  className="input-field"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  required
                />
              </div>
              {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">{error}</div>}
              {success && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4">{success}</div>}
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <div className="reaction-card">
            <h2 className="text-2xl font-bold mb-6">Change Email</h2>
            <div className="mb-4 text-gray-300">
              Current Email: <span className="text-white font-semibold">{user.email}</span>
            </div>
            <form onSubmit={handleEmailUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">New Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={emailForm.new_email}
                  onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <input
                  type="password"
                  className="input-field"
                  value={emailForm.password}
                  onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                  required
                />
              </div>
              {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">{error}</div>}
              {success && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4">{success}</div>}
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Updating...' : 'Update Email'}
              </button>
            </form>
          </div>
        )}

        {/* PIN Tab */}
        {activeTab === 'pin' && (
          <div className="reaction-card">
            <h2 className="text-2xl font-bold mb-6">Support PIN</h2>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mb-6">
              <p className="text-gray-300 mb-4">
                Your Support PIN is used to verify your identity when contacting customer support.
                Keep this PIN secure and provide it to CS when asked.
              </p>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-8 text-center">
              <div className="text-gray-400 mb-2">Your Support PIN</div>
              <div className="text-5xl font-bold text-purple-400 font-mono tracking-wider">
                {user.support_pin || 'N/A'}
              </div>
            </div>
            <div className="mt-6 text-center text-sm text-gray-400">
              ⚠️ Do not share this PIN with anyone except Dexreaction support staff
            </div>
          </div>
        )}

        {/* 2FA Tab */}
        {activeTab === '2fa' && (
          <div className="reaction-card">
            <h2 className="text-2xl font-bold mb-6">Two-Factor Authentication (2FA)</h2>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mb-6">
              <p className="text-gray-300 mb-4">
                Add an extra layer of security to your account by enabling two-factor authentication.
                You'll need to enter a code from your authenticator app when you log in.
              </p>
            </div>

            {!user.two_fa_enabled && !twoFASetup && (
              <div>
                <button onClick={handleSetup2FA} className="btn-primary w-full" disabled={loading}>
                  {loading ? 'Setting up...' : 'Setup 2FA'}
                </button>
              </div>
            )}

            {twoFASetup && !user.two_fa_enabled && (
              <div className="space-y-4">
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Scan QR Code</h3>
                  <div className="flex justify-center mb-4">
                    <img src={twoFASetup.qr_code} alt="QR Code" className="w-64 h-64" />
                  </div>
                  <div className="text-center mb-4">
                    <p className="text-gray-400 text-sm mb-2">Or enter this code manually:</p>
                    <code className="bg-black/30 px-4 py-2 rounded text-purple-400 font-mono">
                      {twoFASetup.secret}
                    </code>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Enter 6-digit code from your app</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="000000"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>

                {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">{error}</div>}
                {success && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4">{success}</div>}

                <button 
                  onClick={handleEnable2FA} 
                  className="btn-primary w-full" 
                  disabled={loading || twoFACode.length !== 6}
                >
                  {loading ? 'Verifying...' : 'Enable 2FA'}
                </button>
              </div>
            )}

            {user.two_fa_enabled && (
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 text-center">
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="text-xl font-semibold mb-2 text-green-400">2FA is Enabled</h3>
                  <p className="text-gray-300">Your account is protected with two-factor authentication</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Enter code to disable 2FA</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="000000"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>

                {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">{error}</div>}
                {success && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4">{success}</div>}

                <button 
                  onClick={handleDisable2FA} 
                  className="btn-secondary w-full border-red-500/50 hover:bg-red-500/20" 
                  disabled={loading || twoFACode.length !== 6}
                >
                  {loading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSettings;
